import type { Map as MapLibreMap } from 'maplibre-gl';
import { interpolateColor, mercatorLat, type ColorStops } from '@/core/utils';
import { EMPTY_FIELD, sampleField, type WindField } from '../model/windField';
import { ViewCanvas, type ViewFrame } from './viewCanvas';

/** Screen pixels per canvas pixel: the wind is smooth, and the map scales the canvas up smoothly. */
const SCREEN_PX_PER_PIXEL = 4;

/**
 * RGBA pixels of the wind speed over a frame, coloured by `stops`; transparent where the wind is unknown.
 * Pixel (x, y) shows the wind at its centre.
 */
export function paintField(field: WindField, stops: ColorStops, f: ViewFrame, width: number, height: number) {
  const px = new Uint8ClampedArray(width * height * 4);
  const wind: [number, number] = [0, 0];
  for (let row = 0; row < height; row++) {
    const lat = mercatorLat(f.y0 + (row + 0.5) * f.unit);
    for (let col = 0; col < width; col++) {
      const lng = (f.x0 + (col + 0.5) * f.unit) * 360 - 180;
      if (!sampleField(field, lng, lat, wind)) continue;
      const c = interpolateColor(stops, Math.hypot(wind[0], wind[1]));
      px.set([c[0], c[1], c[2], 255], (row * width + col) * 4);
    }
  }
  return px;
}

/** The wind speed in colour, on a low-resolution canvas following the view. */
export class WindGradient {
  private readonly view: ViewCanvas;
  private field: WindField = EMPTY_FIELD;

  constructor(
    map: MapLibreMap,
    private readonly stops: ColorStops,
    options: { id: string; beforeId?: string; opacity: number },
  ) {
    this.view = new ViewCanvas(map, {
      ...options,
      padding: 0.5,
      resolution: () => 1 / SCREEN_PX_PER_PIXEL,
      keepPicture: false,
      onFit: () => this.paint(),
    });
  }

  setField(field: WindField) {
    this.field = field;
    this.paint();
  }

  setRunning(running: boolean) {
    this.view.setRunning(running);
  }

  dispose() {
    this.view.dispose();
  }

  private paint() {
    const { canvas, ctx, frame } = this.view;
    const { width, height } = canvas;
    ctx.putImageData(
      new ImageData(paintField(this.field, this.stops, frame, width, height), width, height),
      0,
      0,
    );
  }
}
